import { DestroyRef, inject, Injectable, NgZone } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { BaseRoute } from '@core/data-access/models/routes.model';
import { Store } from '@ngrx/store';
import { HomeComponent } from '@pages/home/home.component';
import { AppConstants } from '@shared/app-constants';
import { getAppVersion } from '@shared/app-utils';
import { SetReloadRequired } from '@shared/services-with-reducers/helpers/helper.actions';
import { hubMethods } from '@shared/signalr/signalr-signals';
import { SignalrService } from '@shared/signalr/signalr.service';
import { CustomizationService } from '@store/customization/customization.service';
import { isEmpty } from 'lodash';
import { combineLatest, filter, first, Subject } from 'rxjs';

@Injectable()
export class MaintenancePageGuard implements CanActivate {
  readonly #destroyRef = inject(DestroyRef);
  readonly #ngZone = inject(NgZone);
  readonly #store = inject(Store);
  readonly #router = inject(Router);
  readonly #customizationService = inject(CustomizationService);
  readonly #signalRService = inject(SignalrService);

  #eventId: string;
  #name = this.constructor.name;

  #canActivate$ = new Subject<boolean>();
  #isReloadRequired$ = new Subject<boolean>();

  constructor() {
    // listen to maintenance change from signalR
    this.handleOperatorMaintenance();
    this.handleEventMaintenance();
    this.handleReloadRequired();

    combineLatest({ canActivate: this.#canActivate$, isReloadRequired: this.#isReloadRequired$ })
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(({ canActivate, isReloadRequired }) => {
        if (!canActivate) {
          this.toMaintenancePage();
        }

        if (canActivate && isReloadRequired) {
          this.#store.dispatch(new SetReloadRequired(isReloadRequired));
        }
      });
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    const canActivatePromise = new Promise<boolean>((resolve) => {
      const urlWithoutParams = state.url.split('?')[0];
      const eventIdFromUrl = urlWithoutParams.split('/')[2];

      if (eventIdFromUrl) {
        this.#eventId = eventIdFromUrl;
      } else {
        this.#eventId = null;
      }

      // get maintenance data from api
      this.handleAlreadyRunningMaintenance();

      combineLatest({ canActivate: this.#canActivate$, isReloadRequired: this.#isReloadRequired$ })
        .pipe(first())
        .subscribe(({ canActivate, isReloadRequired }) => {
          if (canActivate && isReloadRequired) {
            canActivate = route.component === HomeComponent;
          }

          resolve(canActivate);
        });
    });

    return canActivatePromise;
  }

  private handleOperatorMaintenance() {
    this.#signalRService.registerHandler(this.#name, hubMethods.maintenanceOperatorStarted, () => {
      this.#canActivate$.next(false);
    });
  }

  private handleEventMaintenance() {
    this.#signalRService.registerHandler(this.#name, hubMethods.maintenanceEventStarted, (maintenanceData) => {
      if (this.#eventId == maintenanceData) {
        this.#canActivate$.next(false);
      }
    });
  }

  private handleReloadRequired() {
    this.#signalRService.registerHandler(this.#name, hubMethods.reloadRequired, () => {
      this.#isReloadRequired$.next(true);
    });
  }

  private handleAlreadyRunningMaintenance() {
    return combineLatest({
      operatorSettings: this.#customizationService.getOperatorSettings(),
      eventSettings: this.#customizationService.getCustomSettings(this.#eventId)
    })
      .pipe(filter(({ operatorSettings, eventSettings }) => !isEmpty(operatorSettings) && !isEmpty(eventSettings)))
      .subscribe(({ operatorSettings, eventSettings }) => {
        //in case there are no operator settings or event settings we show maintenance page
        if (operatorSettings.maintenance.isPresent || eventSettings.maintenance.isPresent) {
          this.#canActivate$.next(false);
        } else {
          this.#canActivate$.next(true);
        }

        //a page reload is required if:
        //-there's a new version of the application (this is checked here),
        //-or the ReloadRequired message is received via the SignalR communication.
        const version = getAppVersion();
        const oldAppVersion = localStorage.getItem(AppConstants.appVersionReducer);

        if (!(oldAppVersion && oldAppVersion === version)) {
          this.#isReloadRequired$.next(true);
        } else {
          this.#isReloadRequired$.next(false);
        }
      });
  }

  private toMaintenancePage() {
    return this.#ngZone.run(() => this.#router.navigate([`/${BaseRoute.Maintenance}`]));
  }
}
